import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { config } from '@accounts/config';
import { Credentials } from '@accounts/domain';
import { usePresence } from '@accounts/session';
import { Linking, Provider, Refusal } from '@accounts/transport';
import { Button, Callout, Field, Form } from '@beyond-js/ui/react';
import { ActingChoice, Check, NewPassword, Panel, Problem, Retry, removed, useUnread } from '@accounts/ui';

type Open = 'password' | 'email' | 'add' | undefined;

/**
 * The account's password and the address it signs in with, while signed in (ACC-10): change the
 * password (proved by the current one, other devices signed out by default), change the address (it
 * changes once the new one is confirmed), or add email sign-in to an account that has only GitHub or
 * Google (proved by signing in with that provider once more, ACC-04).
 *
 * Nothing still to write is said (D183): while a field of the open form is empty its action waits quietly,
 * a press puts the caret there and nothing is sent; an address that is not one is said in its field, and
 * what the service refuses (a wrong current password, a weak new one) in theirs.
 */
export function Password() {
	const { text, client, identity, renewed } = usePresence();
	const credentials = useMemo(() => new Credentials(client), [client]);
	const [parameters] = useSearchParams();

	const [has, setHas] = useState<boolean>();
	const [providers, setProviders] = useState<string[]>([]);
	// When it was last read, said over what it offers while a later read does not answer
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [open, setOpen] = useState<Open>();
	const [current, setCurrent] = useState('');
	const [next, setNext] = useState('');
	const [address, setAddress] = useState('');
	const [others, setOthers] = useState(true);
	const [acting, setActing] = useState(false);
	const [token, setToken] = useState<string>();
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<unknown>();
	const [invalid, setInvalid] = useState<{ current?: string; next?: string; address?: string }>({});
	const [notice, setNotice] = useState<string>(() => {
		const reset = parameters.get('reset');
		if (reset !== null) {
			const said = text('reset.ended', { count: Number(reset) || 0 });
			// What a recovery that chose it ended besides the sessions (D196)
			const apps = parameters.get('apps');
			return apps === null ? said : `${said} ${removed(text, { apps: Number(apps) || 0, tokens: Number(parameters.get('tokens')) || 0 })}`;
		}
		return parameters.get('email') === 'confirmed' ? text('identities.attached') : '';
	});

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setUnread(Refusal.kept);
		try {
			const answer = await credentials.password();
			setHas(answer.password);
			setProviders(answer.providers);
			setAt(Date.now());
			setUnread(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		}
	}, [credentials]);

	// Accounts not answering is the page's to say once; this section says it in its place (D163)
	const outage = useUnread({ key: 'password', title: text('password.title'), unread, run: read });

	useEffect(() => {
		void read();
	}, [read, renewed]);

	// Back from the provider round trip that proves this account before a password is added
	useEffect(() => {
		if (Linking.purpose() !== 'password') return;
		const returned = Provider.returned();
		Linking.end();
		if (!returned.token && !returned.code) return;
		const proved = returned.token ? Promise.resolve(returned.token) : new Provider(config.provider, config.key).exchange(returned.code as string);
		proved.then(value => {
			setToken(value);
			setOpen('add');
		}, setFailure);
	}, []);

	function reset(after?: string) {
		setCurrent('');
		setNext('');
		setAddress('');
		// Ending what acts for the person is asked afresh each time, never carried from an earlier change
		setActing(false);
		setInvalid({});
		setOpen(undefined);
		if (after) setNotice(after);
	}

	async function run(work: () => Promise<string>) {
		setBusy(true);
		setFailure(undefined);
		try {
			reset(await work());
		} catch (error) {
			setCurrent('');
			if (Refusal.is(error) && error.code === 'PASSWORD_WEAK') setInvalid({ next: text(error.key, error.values) });
			else if (Refusal.is(error) && error.code === 'FACTOR_INVALID') setInvalid({ current: text('password.wrong') });
			else if (!(Refusal.is(error) && error.quiet)) setFailure(error);
		} finally {
			setBusy(false);
		}
	}

	const change = () =>
		run(async () => {
			const answer = await credentials.change(current, next, !others, acting);
			const said = others ? text('password.changed', { count: answer.ended }) : text('password.changed.kept');
			return answer.access ? `${said} ${removed(text, answer.access)}` : said;
		});
	// Both fields hold something here: an empty one stopped the submission in `Form`, saying nothing
	const move = () => {
		const wrong = !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address);
		setInvalid(wrong ? { address: text('error.field.email') } : {});
		if (wrong) return undefined;
		return run(async () => {
			const answer = await credentials.email(address, current);
			return text('email.pending', { email: answer.pending });
		});
	};
	const add = () =>
		run(async () => {
			await credentials.add(token as string, next);
			setHas(true);
			setToken(undefined);
			return text('password.added');
		});

	/** Leaves to sign in with the account's own provider once more, then comes back here to choose a password */
	async function prove() {
		Linking.begin(globalThis.sessionStorage, 'password');
		const provider = providers.find(name => name !== 'email') ?? 'github';
		globalThis.location.href = await new Provider(config.provider, config.key).authorize(provider, globalThis.location.href);
	}

	if (unread && !outage) return <Retry title={text(unread.key)} label={text('action.retry')} busy={text('state.retrying')} onRetry={read} error={unread} />;
	const email = identity?.account.email;

	// Its heading and the address are known at once; what it offers waits for the read (LOAD-01)
	return (
		<Panel
			id="password"
			title={text('password.title')}
			description={email ? text('email.current', { email }) : undefined}
			waiting={has === undefined && !unread}
			unavailable={outage && has === undefined}
			updated={outage && has !== undefined ? (at ?? null) : undefined}
		>
			{notice ? <Callout tone="success" live title={notice} /> : null}
			{failure ? <Problem error={failure} /> : null}
			{has ? (
				<div className="flex flex-wrap gap-2">
					<Button variant="secondary" onClick={() => setOpen(open === 'password' ? undefined : 'password')} aria-expanded={open === 'password'}>
						{text('password.change')}
					</Button>
					<Button variant="secondary" onClick={() => setOpen(open === 'email' ? undefined : 'email')} aria-expanded={open === 'email'}>
						{text('email.change')}
					</Button>
				</div>
			) : !token ? (
				<div className="flex flex-col items-start gap-2">
					<p className="max-w-reading text-body text-ink-muted">{text('password.add.lead', { provider: text(`identities.provider.${providers.find(name => name !== 'email') ?? 'github'}`) })}</p>
					<Button variant="secondary" onClick={() => void prove()}>
						{text('password.add')}
					</Button>
				</div>
			) : null}

			{open === 'password' || open === 'email' || open === 'add' ? (
				<Form
					className="mt-4 flex max-w-form flex-col gap-4"
					onSubmit={event => {
						event.preventDefault();
						void (open === 'password' ? change() : open === 'email' ? move() : add());
					}}
				>
					{open === 'add' ? <p className="text-body text-ink">{email ? text('password.add.choose', { email }) : text('password.add.plain')}</p> : null}
					{open === 'email' ? (
						<Field label={text('email.new')} error={invalid.address}>
							<input type="email" autoComplete="email" required value={address} onChange={event => setAddress(event.target.value)} />
						</Field>
					) : null}
					{open !== 'add' ? (
						<Field label={text('password.current')} error={invalid.current}>
							<input type="password" autoComplete="current-password" required value={current} onChange={event => setCurrent(event.target.value)} />
						</Field>
					) : null}
					{open !== 'email' ? <NewPassword label={text('password.new')} value={next} onChange={setNext} error={invalid.next} required /> : null}
					{open === 'password' ? <Check label={text('password.others')} checked={others} onChange={setOthers} /> : null}
					{open === 'password' ? <ActingChoice checked={acting} onChange={setActing} linked /> : null}
					<div className="flex flex-wrap gap-2">
						<Button type="submit" variant="primary" busy={busy} labels={{ busy: text('action.saving') }}>
							{text(open === 'password' ? 'password.change' : open === 'email' ? 'email.change' : 'password.add')}
						</Button>
						<Button type="button" variant="quiet" onClick={() => reset()} disabled={busy}>
							{text('action.cancel')}
						</Button>
					</div>
				</Form>
			) : null}
		</Panel>
	);
}
